import {
  useCallback,
  useEffect,
  useRef,
  useState,
  type CSSProperties,
  type FormEvent,
  type ReactNode
} from "react";
import { useAuth } from "../auth/AuthProvider.js";
import { Badge, Button, Card, DataTable, Field, Modal } from "../components/index.js";
import type { Column } from "../components/index.js";
import { ApiFetchError } from "../lib/api.js";
import {
  EMPTY_USER_FORM,
  hasUserFormErrors,
  mapUserApiError,
  roleLabel,
  toCreateUserInput,
  userInitials,
  validateUserForm,
  type UserFormErrors,
  type UserFormValues
} from "../lib/user-form.js";
import type { UserDto, UserRole } from "../types.js";

/**
 * Cadastro › Usuários (Design.md §7). ADMIN-only: a rota é guardada por
 * RequireRole e o backend (users.ts) é ADMIN-only em list/create/delete. Layout
 * em duas colunas — formulário "Cadastrar usuário" + tabela "Usuários
 * cadastrados". Toda a microcopy de validação espelha o backend (pt-BR, §10).
 */
export function UsuariosPage(): ReactNode {
  const { api, me } = useAuth();

  const [users, setUsers] = useState<UserDto[]>([]);
  const [activeCount, setActiveCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  // Sequência de requisição: ignora respostas obsoletas quando a lista é
  // recarregada em rajada (cadastro/remoção) e protege contra setState após
  // desmontar (mesmo padrão de GerenciarEnviosPage).
  const reqSeq = useRef(0);

  const load = useCallback(async () => {
    const seq = ++reqSeq.current;
    setLoading(true);
    setLoadError(null);
    try {
      const res = await api.listUsers();
      if (seq !== reqSeq.current) return;
      setUsers(res.users);
      setActiveCount(res.activeCount);
    } catch (error: unknown) {
      if (seq !== reqSeq.current) return;
      setLoadError(
        error instanceof Error ? error.message : "Falha ao carregar os usuários."
      );
    } finally {
      if (seq === reqSeq.current) setLoading(false);
    }
  }, [api]);

  useEffect(() => {
    void load();
  }, [load]);

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: "1.25rem" }}>
      <header>
        <h2 style={{ fontSize: 21, fontWeight: 700 }}>Usuários</h2>
        <p style={{ fontSize: 12.5, color: "var(--text-muted)", marginTop: "0.2rem" }}>
          Allowlist interna · acesso restrito (Admin)
        </p>
      </header>

      <div
        style={{
          display: "grid",
          gridTemplateColumns: "minmax(280px, 360px) 1fr",
          gap: "1.25rem",
          alignItems: "start"
        }}
      >
        <CreateUserForm onCreated={() => void load()} />
        <UsersTable
          users={users}
          activeCount={activeCount}
          loading={loading}
          loadError={loadError}
          currentEmail={me?.email ?? null}
          onChanged={() => void load()}
        />
      </div>
    </div>
  );
}

function CreateUserForm({ onCreated }: { onCreated: () => void }): ReactNode {
  const { api } = useAuth();
  const [values, setValues] = useState<UserFormValues>(EMPTY_USER_FORM);
  const [errors, setErrors] = useState<UserFormErrors>({});
  const [generalError, setGeneralError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  function update<K extends keyof UserFormValues>(
    key: K,
    value: UserFormValues[K]
  ): void {
    setValues((prev) => ({ ...prev, [key]: value }));
    setSuccess(null);
  }

  async function handleSubmit(event: FormEvent<HTMLFormElement>): Promise<void> {
    event.preventDefault();
    setGeneralError(null);
    setSuccess(null);

    const validation = validateUserForm(values);
    if (hasUserFormErrors(validation)) {
      setErrors(validation);
      return;
    }
    setErrors({});

    setSubmitting(true);
    try {
      const res = await api.createUser(toCreateUserInput(values));
      setValues(EMPTY_USER_FORM);
      setSuccess(`${res.user.name} cadastrado com sucesso.`);
      onCreated();
    } catch (error: unknown) {
      if (error instanceof ApiFetchError) {
        const mapped = mapUserApiError({
          code: error.code,
          message: error.message,
          fields: error.fields
        });
        setErrors(mapped.fieldErrors);
        setGeneralError(mapped.generalMessage);
      } else {
        setGeneralError(
          error instanceof Error ? error.message : "Falha ao cadastrar o usuário."
        );
      }
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <Card>
      <h3 style={{ fontSize: 15, fontWeight: 700, marginBottom: "0.85rem" }}>
        Cadastrar usuário
      </h3>
      <form
        onSubmit={handleSubmit}
        noValidate
        style={{ display: "flex", flexDirection: "column", gap: "0.85rem" }}
      >
        <Field
          label="Nome completo"
          htmlFor="user-name"
          required
          {...(errors.name !== undefined ? { error: errors.name } : {})}
        >
          <input
            id="user-name"
            type="text"
            value={values.name}
            onChange={(e) => update("name", e.target.value)}
            autoComplete="name"
          />
        </Field>

        <Field
          label="E-mail"
          htmlFor="user-email"
          required
          {...(errors.email !== undefined ? { error: errors.email } : {})}
        >
          <input
            id="user-email"
            type="email"
            value={values.email}
            onChange={(e) => update("email", e.target.value)}
            autoComplete="off"
          />
        </Field>

        <Field label="Função" htmlFor="user-role">
          <select
            id="user-role"
            value={values.role}
            onChange={(e) => update("role", e.target.value as UserRole)}
          >
            <option value="OPERATOR">Operador</option>
            <option value="ADMIN">Admin</option>
          </select>
        </Field>

        {generalError !== null ? (
          <p role="alert" style={{ fontSize: 12, color: "var(--orange)", fontWeight: 600 }}>
            {generalError}
          </p>
        ) : null}

        {success !== null ? (
          <p role="status" style={{ fontSize: 12, color: "var(--green)", fontWeight: 600 }}>
            ✓ {success}
          </p>
        ) : null}

        <Button type="submit" disabled={submitting}>
          {submitting ? "Cadastrando…" : "Cadastrar usuário"}
        </Button>
      </form>
    </Card>
  );
}

function UsersTable({
  users,
  activeCount,
  loading,
  loadError,
  currentEmail,
  onChanged
}: {
  users: UserDto[];
  activeCount: number;
  loading: boolean;
  loadError: string | null;
  currentEmail: string | null;
  onChanged: () => void;
}): ReactNode {
  const { api } = useAuth();
  const [removing, setRemoving] = useState<string | null>(null);
  const [rowError, setRowError] = useState<string | null>(null);
  // Confirmação explícita em ação irreversível (Design.md §9/§10) via Modal do
  // design system — não usar window.confirm.
  const [confirmTarget, setConfirmTarget] = useState<UserDto | null>(null);

  async function handleRemove(user: UserDto): Promise<void> {
    setRowError(null);
    setRemoving(user.email);
    try {
      await api.deleteUser(user.email);
      setConfirmTarget(null);
      onChanged();
    } catch (error: unknown) {
      setConfirmTarget(null);
      setRowError(
        error instanceof Error ? error.message : "Falha ao remover o usuário."
      );
    } finally {
      setRemoving(null);
    }
  }

  const columns: readonly Column<UserDto>[] = [
    {
      key: "user",
      header: "Usuário",
      render: (u) => (
        <div style={{ display: "flex", alignItems: "center", gap: "0.6rem" }}>
          <Avatar name={u.name} />
          <div style={{ display: "flex", flexDirection: "column" }}>
            <span style={{ fontWeight: 600 }}>{u.name}</span>
            <span className="mono" style={{ fontSize: 11.5, color: "var(--text-muted)" }}>
              {u.email}
            </span>
          </div>
        </div>
      )
    },
    {
      key: "role",
      header: "Função",
      render: (u) => (
        <Badge tone={u.role === "ADMIN" ? "orange" : "blue"}>{roleLabel(u.role)}</Badge>
      )
    },
    {
      key: "actions",
      header: "Ações",
      align: "right",
      render: (u) => {
        const isSelf =
          currentEmail !== null &&
          u.email.trim().toLowerCase() === currentEmail.trim().toLowerCase();
        return (
          <Button
            variant="secondary"
            disabled={isSelf || removing === u.email}
            {...(isSelf ? { title: "Você não pode remover o próprio usuário." } : {})}
            onClick={() => setConfirmTarget(u)}
          >
            {removing === u.email ? "Removendo…" : "Remover"}
          </Button>
        );
      }
    }
  ];

  return (
    <Card>
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          marginBottom: "0.85rem"
        }}
      >
        <h3 style={{ fontSize: 15, fontWeight: 700 }}>Usuários cadastrados</h3>
        <span style={{ fontSize: 12, color: "var(--text-muted)" }}>
          {activeCount} ativo{activeCount === 1 ? "" : "s"}
        </span>
      </div>

      {loadError !== null ? (
        <p role="alert" style={{ color: "var(--orange)", fontSize: 12.5 }}>
          {loadError}
        </p>
      ) : null}

      {rowError !== null ? (
        <p role="alert" style={{ color: "var(--orange)", fontSize: 12.5, marginBottom: "0.5rem" }}>
          {rowError}
        </p>
      ) : null}

      <DataTable
        columns={columns}
        rows={users}
        getRowKey={(u) => u.email}
        empty={loading ? "Carregando…" : "Nenhum usuário cadastrado."}
      />

      <Modal
        open={confirmTarget !== null}
        onClose={() => setConfirmTarget(null)}
        title="Remover usuário"
        width={420}
        footer={
          <>
            <Button variant="secondary" onClick={() => setConfirmTarget(null)}>
              Cancelar
            </Button>
            <Button
              disabled={removing !== null}
              onClick={() => {
                if (confirmTarget !== null) void handleRemove(confirmTarget);
              }}
            >
              {removing !== null ? "Removendo…" : "Remover"}
            </Button>
          </>
        }
      >
        {confirmTarget !== null ? (
          <p style={{ fontSize: 13, color: "var(--text)" }}>
            Remover o acesso de <strong>{confirmTarget.name}</strong> (
            <span className="mono">{confirmTarget.email}</span>)? Esta ação não
            pode ser desfeita.
          </p>
        ) : null}
      </Modal>
    </Card>
  );
}

function Avatar({ name }: { name: string }): ReactNode {
  const style: CSSProperties = {
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    width: 32,
    height: 32,
    borderRadius: "50%",
    background: "var(--blue-soft)",
    color: "var(--blue)",
    fontSize: 11.5,
    fontWeight: 700,
    flexShrink: 0
  };
  return (
    <span aria-hidden style={style}>
      {userInitials(name)}
    </span>
  );
}
